import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Optional,
  input,
  model,
  computed,
  signal,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  InputCheckboxComponent,
  SelectBoxComponent,
  InputTextComponent,
} from '@pedix-workspace/angular-ui-form-reactive';
import { AbstractControl, ControlContainer, FormsModule, NgForm } from '@angular/forms';
import {
  ARCA_DOCUMENT_TYPE_CUIL,
  ARCA_DOCUMENT_TYPE_CUIT,
  ARCA_DOCUMENT_TYPE_DNI,
  ARCA_VAT_CONDITION_CONSUMIDOR_FINAL,
  ARCA_VAT_CONDITION_RESPONSABLE_INSCRIPTO,
  arcaDocumentTypeOptions,
  arcaVatConditionOptions,
  cuitValidator,
  isArcaVatConditionRequiringCuit,
} from '@pedix-workspace/integrations-arca';
import { combineLatest, skip } from 'rxjs';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BillingInformation } from '@pedix-workspace/utils';

@Component({
  selector: 'pxw-end-order-billing-fields',
  templateUrl: './end-order-billing-fields.component.html',
  styleUrls: ['./end-order-billing-fields.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    InputTextComponent,
    InputCheckboxComponent,
    SelectBoxComponent,
    FormsModule,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class EndOrderBillingFieldsComponent implements OnInit {
  readonly isFormSubmitted = input<boolean>(false);

  readonly billingInformation = model.required<BillingInformation | null>();
  readonly requestBilling = model.required<boolean>();
  readonly isAdminMode = input.required<boolean>();

  readonly billingName = signal<string | null>(null);
  readonly documentType = signal<BillingInformation['documentType']>(ARCA_DOCUMENT_TYPE_CUIT);
  readonly documentNumber = signal<string | null>(null);
  readonly billingAddress = signal<string | null>(null);
  readonly billingEmail = signal<string | null>(null);
  readonly vatCondition = signal<BillingInformation['vatCondition']>(
    ARCA_VAT_CONDITION_RESPONSABLE_INSCRIPTO,
  );

  readonly displayRequestBilling = computed(() => {
    return !this.isAdminMode();
  });
  readonly vatConditionOptions = computed(() => {
    return [...arcaVatConditionOptions];
  });
  readonly documentTypeOptions = computed(() => {
    if (this.isVatConditionBusiness()) {
      return arcaDocumentTypeOptions.filter(option => option.id === ARCA_DOCUMENT_TYPE_CUIT);
    }
    return [...arcaDocumentTypeOptions];
  });
  readonly documentValidator = computed(() => {
    if (
      this.documentType() === ARCA_DOCUMENT_TYPE_CUIT ||
      this.documentType() === ARCA_DOCUMENT_TYPE_CUIL
    ) {
      return this.cuitValidator;
    }
    return undefined;
  });
  readonly documentPattern = computed(() => {
    if (this.documentType() === ARCA_DOCUMENT_TYPE_DNI) {
      return this.dniPattern;
    }
    return undefined;
  });
  readonly isVatConditionBusiness = computed(() => {
    return isArcaVatConditionRequiringCuit(this.vatCondition());
  });

  protected dniPattern = /^[0-9]{7,8}$/;
  protected emailPattern = /^[\w.-]+@[\w.-]+\.\w{2,4}$/i;

  constructor() {
    combineLatest({
      vatCondition: toObservable(this.vatCondition),
      billingName: toObservable(this.billingName),
      documentType: toObservable(this.documentType),
      documentNumber: toObservable(this.documentNumber),
      billingAddress: toObservable(this.billingAddress),
      billingEmail: toObservable(this.billingEmail),
    })
      .pipe(takeUntilDestroyed())
      .subscribe(
        ({
          vatCondition,
          billingName,
          documentType,
          documentNumber,
          billingAddress,
          billingEmail,
        }) => {
          this.billingInformation.set({
            provider: 'arca',
            name: billingName,
            documentType: documentType,
            documentNumber: documentNumber ? parseInt(documentNumber, 10) : null,
            vatCondition: vatCondition,
            address: billingAddress,
            email: billingEmail,
          });
        },
      );

    toObservable(this.vatCondition)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.isVatConditionBusiness()) {
          this.documentType.set(ARCA_DOCUMENT_TYPE_CUIT);
        } else {
          this.documentType.set(ARCA_DOCUMENT_TYPE_DNI);
        }
      });
    toObservable(this.documentType)
      .pipe(skip(1), takeUntilDestroyed())
      .subscribe(() => {
        this.documentNumber.set('');
      });
  }

  ngOnInit(): void {
    const billingInformation = this.billingInformation();

    if (billingInformation) {
      this.vatCondition.set(billingInformation.vatCondition);
      this.billingName.set(billingInformation.name || null);
      this.documentType.set(billingInformation.documentType);
      this.documentNumber.set(billingInformation.documentNumber?.toString() || null);
      this.billingAddress.set(billingInformation.address || null);
      this.billingEmail.set(billingInformation.email || null);
    } else {
      this.vatCondition.set(ARCA_VAT_CONDITION_CONSUMIDOR_FINAL);
    }
    if (this.isAdminMode()) {
      this.requestBilling.set(true);
    }
  }

  satinizeCuit() {
    this.documentNumber.update(prev => (prev || '').toString().replace(/[^0-9]/g, '') || '');
  }

  // NOTE: this needs to be bound to the class using arrow function
  cuitValidator = (control: AbstractControl) => {
    const cuit = control.value?.toString() || '';

    return cuitValidator(cuit);
  };
}
